import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, Injector, OnInit, Signal, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Address } from '../../core/models/address.model';
import { ChangeLogEntry, ChangeSet } from '../../core/models/change-log.model';
import { Interaction } from '../../core/models/interaction.model';
import { InteractionType } from '../../core/models/interaction-type.model';
import { Objection } from '../../core/models/objection.model';
import { Tag } from '../../core/models/tag.model';
import { AddressService } from '../../core/services/address.service';
import { AuthService } from '../../core/services/auth.service';
import { BreadcrumbService } from '../../core/services/breadcrumb.service';
import { ChangeLogService } from '../../core/services/change-log.service';
import { ClockService } from '../../core/services/clock.service';
import { InteractionService } from '../../core/services/interaction.service';
import { InteractionTypeService } from '../../core/services/interaction-type.service';
import { LoggerService } from '../../core/services/logger.service';
import { ObjectionService } from '../../core/services/objection.service';
import { PermissionService } from '../../core/services/permission.service';
import { TagService } from '../../core/services/tag.service';
import { OrganizationUser, UserService } from '../../core/services/user.service';
import { AttachmentStripComponent } from '../../shared/components/attachment-strip/attachment-strip.component';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../shared/components/card/card-title.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { ModalDialogComponent } from '../../shared/components/modal-dialog/modal-dialog.component';
import { OutcomeChipComponent } from '../../shared/components/outcome-chip/outcome-chip.component';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { TagChipComponent } from '../../shared/components/tag-chip/tag-chip.component';
import { HasPermissionDirective } from '../../shared/directives/has-permission.directive';
import { DateTimezonePipe } from '../../shared/pipes/date-timezone.pipe';
import { RelativeTimePipe } from '../../shared/pipes/relative-time.pipe';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

/** One row of the merged timeline. */
export type TimelineItem =
  | { kind: 'interaction'; at: number; interaction: Interaction }
  | { kind: 'change'; at: number; entry: ChangeLogEntry; changes: ChangeSet };

/**
 * Address detail: header, do-not-contact banner, and a timeline that merges
 * interactions with change-log entries. Every source is a reactive query so
 * incoming sync changes land without a reload.
 */
@Component({
  selector: 'app-address-detail',
  standalone: true,
  imports: [
    RouterLink,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    ButtonComponent,
    IconComponent,
    SpinnerComponent,
    ModalDialogComponent,
    OutcomeChipComponent,
    TagChipComponent,
    AttachmentStripComponent,
    HasPermissionDirective,
    DateTimezonePipe,
    RelativeTimePipe,
    TranslatePipe,
  ],
  templateUrl: './address-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './address-detail.component.css',
})
export class AddressDetailComponent implements OnInit {
  private addressService = inject(AddressService);
  private interactionService = inject(InteractionService);
  private changeLogService = inject(ChangeLogService);
  private objectionService = inject(ObjectionService);
  private interactionTypeService = inject(InteractionTypeService);
  private tagService = inject(TagService);
  private userService = inject(UserService);
  private authService = inject(AuthService);
  private permissionService = inject(PermissionService);
  private breadcrumbService = inject(BreadcrumbService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private location = inject(Location);
  private injector = inject(Injector);
  private log = inject(LoggerService).getLogger('address-detail');

  readonly now = inject(ClockService).now;

  addressId = '';
  address!: Signal<Address | null>;
  addressLoading!: Signal<boolean>;
  interactions!: Signal<Interaction[]>;
  changes!: Signal<ChangeLogEntry[]>;
  objections!: Signal<Objection[]>;
  interactionTypes!: Signal<InteractionType[]>;
  tagsByInteraction!: Signal<Map<string, Tag[]>>;
  users!: Signal<OrganizationUser[]>;

  showClearDncDialog = signal(false);
  clearingDnc = signal(false);

  canReadOthers = false;

  timeline = computed<TimelineItem[]>(() => {
    const items: TimelineItem[] = [
      ...this.interactions().map((i) => ({ kind: 'interaction' as const, at: i.occurred_at, interaction: i })),
      ...this.changes().map((entry) => ({
        kind: 'change' as const,
        at: entry.changed_at,
        entry,
        changes: this.changeLogService.parseChanges(entry),
      })),
    ];
    return items.sort((a, b) => b.at - a.at);
  });

  objectionLabels = computed(() => new Map(this.objections().map((o) => [o.id, o.label])));
  typeLabels = computed(() => new Map(this.interactionTypes().map((t) => [t.id, t.label])));
  userNames = computed(() => new Map(this.users().map((u) => [u.id, u.full_name || u.email])));

  async ngOnInit(): Promise<void> {
    this.addressId = this.route.snapshot.paramMap.get('id') ?? '';
    this.canReadOthers = this.permissionService.has('interaction.read-others');
    const onlyUserId = this.canReadOthers ? undefined : (this.authService.getCurrentUserId() ?? '');

    const addressQuery = this.addressService.createQueryById(this.addressId);
    this.address = addressQuery.data;
    this.addressLoading = addressQuery.loading;
    this.interactions = this.interactionService.createQueryForAddress(this.addressId, onlyUserId).data;
    this.changes = this.changeLogService.createQueryFor('address', this.addressId).data;
    this.objections = this.objectionService.createQuery().data;
    this.interactionTypes = this.interactionTypeService.createQuery().data;
    this.tagsByInteraction = this.tagService.createForAddressQuery(this.addressId).data;
    this.users = this.userService.createQuery(true).data;

    // Reactive rather than a one-off snapshot: a freshly pinned address starts with a
    // placeholder formatted_address that reverse geocoding fills in moments later, and
    // the breadcrumb must pick up that update instead of staying stuck on the placeholder.
    effect(
      () => {
        const address = this.address();
        if (address) this.breadcrumbService.setDynamicLabel(address.formatted_address);
      },
      { injector: this.injector },
    );
  }

  goBack(): void {
    this.location.back();
  }

  changeFields(changes: ChangeSet): string[] {
    return Object.keys(changes);
  }

  changeValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return '—';
    return String(value);
  }

  /** The do-not-contact gate now lives in the interaction screen itself
   *  (it needs to run there anyway, for deep links that skip this page
   *  entirely) -- this just navigates.
   *
   *  Absolute, not relative: relative '..' navigation from this component's
   *  own route (the empty-path child of the multi-segment 'addresses/:id'
   *  route) pops a raw URL segment rather than stepping out to the parent's
   *  logical position, which silently drops the :id -- confirmed via a
   *  Playwright repro during this refactor. Absolute paths sidestep it. */
  logInteraction(): void {
    this.router.navigate(['/map/addresses', this.addressId, 'interactions', 'new']);
  }

  /** Outcome is already fixed for an existing interaction, so its edit link
   *  goes straight to the matching subview -- no tile-selection screen to
   *  pass through, unlike a fresh interaction. Absolute for the same reason
   *  as logInteraction() above. */
  interactionEditLink(interaction: Interaction): string[] {
    const base = ['/map/addresses', this.addressId, 'interactions', interaction.id];
    if (interaction.outcome_kind === 'objection') return [...base, 'objection'];
    if (interaction.outcome_kind === 'lead') return [...base, 'lead'];
    return base;
  }

  openClearDnc(): void {
    this.showClearDncDialog.set(true);
  }

  cancelClearDnc(): void {
    this.showClearDncDialog.set(false);
  }

  async confirmClearDnc(): Promise<void> {
    this.clearingDnc.set(true);
    try {
      await this.addressService.clearDoNotContact(this.addressId);
      this.showClearDncDialog.set(false);
    } catch (err) {
      this.log.error('Failed to clear do-not-contact', err);
    } finally {
      this.clearingDnc.set(false);
    }
  }
}
